const KEY = 'vs:creator:deviceId';

// In-memory fallback so a private-browsing session (where every
// localStorage call throws SecurityError) still has a stable id for the
// lifetime of the tab. Without this, every getDeviceId() would mint a
// fresh uuid and every API call would look like a new device.
let inMemoryDeviceId: string | null = null;

function safeLsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — fall through to the in-memory mirror */
  }
}

function readCookie(): string | null {
  try {
    const m = document.cookie.match(/(?:^|; )vs_did=([^;]+)/);
    return m && m[1] ? decodeURIComponent(m[1]) : null;
  } catch {
    // decodeURIComponent throws on a malformed percent-escape — a stray
    // bad cookie should not block boot.
    return null;
  }
}

function writeCookie(value: string): void {
  try {
    // `Secure` keeps the device cookie off plaintext HTTP. It is omitted on
    // localhost so dev (served over http) still works — every real
    // deployment is HTTPS, where the attribute applies.
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `vs_did=${value}; max-age=31536000; SameSite=Lax; Path=/${secure}`;
  } catch {
    /* document.cookie can throw in unusual sandboxed contexts */
  }
}

// Exported so the rest of the creator (sessionId minting in
// useGenerationSession, telemetry probe ids) can share the same
// bulletproof generator. Phase 10 — previously every call site
// reached for `crypto.randomUUID()` directly which throws on iOS 13
// Safari / older WebViews and orphaned the stream.
export function freshUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // Fallback for browsers without crypto.randomUUID (very old Safari,
  // some embedded WebViews). Uses getRandomValues if available, then
  // Math.random as the last resort — quality matters less than boot.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      // RFC4122 v4 shape
      bytes[6] = (bytes[6]! & 0x0f) | 0x40;
      bytes[8] = (bytes[8]! & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    /* fall through */
  }
  // Last-resort uuid using Math.random. Not crypto-grade but it lets
  // the app boot in environments that have stripped Web Crypto.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Tell the gateway about a freshly minted deviceId. The endpoint
 * `/v1/device/init` is currently un-implemented at the gateway, which
 * surfaced as a 405 console error on every cold start (NIGHT_AUDIT
 * 34/100 follow-up P0.4). Until the gateway grows the route, we no-op
 * this announcer — device-credential binding deferred to first real
 * request like the original fallback comment described. Re-enable by
 * removing the early return below.
 */
function announceNewDevice(_deviceId: string): void {
  return;
}

/**
 * Initialize the device id. Bulletproof against:
 *   • localStorage that throws on every access (private browsing Safari)
 *   • crypto.randomUUID missing (very old browsers)
 *   • document.cookie unavailable (data: URLs, some sandboxes)
 * Returns SOMETHING even in the worst case — boot must never block here.
 */
export function initDeviceId(): string {
  try {
    const cookie = readCookie();
    if (cookie) {
      safeLsSet(KEY, cookie);
      inMemoryDeviceId = cookie;
      return cookie;
    }
    const existing = safeLsGet(KEY) ?? inMemoryDeviceId;
    if (existing) {
      writeCookie(existing);
      inMemoryDeviceId = existing;
      return existing;
    }
    const fresh = freshUuid();
    safeLsSet(KEY, fresh);
    writeCookie(fresh);
    inMemoryDeviceId = fresh;
    announceNewDevice(fresh);
    return fresh;
  } catch {
    // Catastrophic fallback — return a session-scoped id so the rest of
    // boot can proceed. The user is on a heavily-locked-down browser; we
    // give up persistence but never give up booting.
    if (!inMemoryDeviceId) inMemoryDeviceId = freshUuid();
    return inMemoryDeviceId;
  }
}

export function getDeviceId(): string {
  try {
    const existing = safeLsGet(KEY) ?? inMemoryDeviceId;
    return existing ?? initDeviceId();
  } catch {
    return inMemoryDeviceId ?? initDeviceId();
  }
}
